import {
  AppState,
  CameraState,
  OBJECTS,
  WORLD,
  clamp,
  lerp,
  smooth
} from './model.js';

const TAU = Math.PI * 2;

export class WorldRenderer {
  constructor(private readonly ctx: CanvasRenderingContext2D) {}

  draw(state: AppState, now: number): void {
    const c = this.ctx;
    c.clearRect(0, 0, state.w, state.h);

    if (state.mode === 'microscope') {
      this.drawMicroscope(state, now);
      return;
    }

    c.save();
    c.translate(state.w / 2, state.h / 2);
    c.scale(state.camera.z, state.camera.z);
    c.translate(-state.camera.x, -state.camera.y);

    this.drawGround(now);
    this.drawPaths();
    this.drawBoundaries();
    this.drawEntrance();
    this.drawRockGarden(state, now);
    this.drawSeedlingPlot(state);
    this.drawMimosa(state, now);
    this.drawSeedBox(state);
    this.drawFlowerPlot(state, now);
    this.drawPond(state, now);
    this.drawMicroscopeStation(state, now);
    this.drawPlayer(state, now);

    c.restore();
    this.drawVignette(state.w, state.h);
  }

  private drawGround(now: number): void {
    const c = this.ctx;
    const g = c.createLinearGradient(0, 0, WORLD.w, WORLD.h);
    g.addColorStop(0, '#8aa86d');
    g.addColorStop(0.42, '#6f995d');
    g.addColorStop(1, '#5f8854');
    c.fillStyle = g;
    c.fillRect(0, 0, WORLD.w, WORLD.h);

    c.fillStyle = 'rgba(238,226,174,.18)';
    for (let y = 90; y < WORLD.h; y += 150) {
      for (let x = 90; x < WORLD.w; x += 165) {
        const k = Math.sin(x * 0.013 + y * 0.017) * 9;
        c.beginPath();
        c.ellipse(x + k, y - k * 0.4, 5, 12, 0.7, 0, TAU);
        c.fill();
      }
    }

    c.strokeStyle = 'rgba(45,88,48,.28)';
    c.lineWidth = 4;
    for (let i = 0; i < 46; i += 1) {
      const x = 65 + ((i * 197) % 2820);
      const y = 55 + ((i * 113) % 1710);
      const sway = Math.sin(now * 0.0014 + i) * 3;
      c.beginPath();
      c.moveTo(x, y + 10);
      c.quadraticCurveTo(x + sway, y, x + sway * 1.2, y - 8);
      c.stroke();
    }
  }

  private drawPaths(): void {
    const c = this.ctx;
    c.save();
    c.lineCap = 'round';
    c.lineJoin = 'round';

    const path = new Path2D();
    path.moveTo(360, 1700);
    path.bezierCurveTo(540, 1510, 770, 1530, 950, 1360);
    path.bezierCurveTo(1160, 1160, 1340, 1190, 1570, 1280);
    path.bezierCurveTo(1840, 1390, 2070, 1425, 2300, 1320);
    path.bezierCurveTo(2520, 1215, 2590, 1020, 2460, 900);

    const branchA = new Path2D();
    branchA.moveTo(1000, 1340);
    branchA.bezierCurveTo(870, 1130, 810, 920, 800, 690);

    const branchB = new Path2D();
    branchB.moveTo(1430, 1230);
    branchB.bezierCurveTo(1510, 1040, 1610, 900, 1690, 760);

    const branchC = new Path2D();
    branchC.moveTo(2050, 1400);
    branchC.bezierCurveTo(2210, 1180, 2320, 1010, 2420, 830);

    c.strokeStyle = 'rgba(72,60,38,.18)';
    c.lineWidth = 126;
    c.stroke(path);
    c.stroke(branchA);
    c.stroke(branchB);
    c.stroke(branchC);

    c.strokeStyle = '#d3c89b';
    c.lineWidth = 112;
    c.stroke(path);
    c.stroke(branchA);
    c.stroke(branchB);
    c.stroke(branchC);

    c.strokeStyle = 'rgba(255,248,210,.30)';
    c.lineWidth = 5;
    c.setLineDash([18, 28]);
    c.stroke(path);
    c.stroke(branchA);
    c.stroke(branchB);
    c.stroke(branchC);
    c.restore();
  }

  private drawBoundaries(): void {
    const c = this.ctx;
    c.save();
    c.strokeStyle = 'rgba(53,72,40,.36)';
    c.lineWidth = 18;
    c.lineCap = 'round';

    for (const y of [260, 380, 500]) {
      c.beginPath();
      c.moveTo(160, y);
      c.lineTo(500, y + 20);
      c.stroke();
    }
    for (let x = 170; x <= 500; x += 82) {
      c.fillStyle = '#756248';
      c.fillRect(x, 215, 16, 330);
    }

    c.strokeStyle = 'rgba(89,74,44,.42)';
    c.lineWidth = 12;
    c.beginPath();
    c.moveTo(1340, 490);
    c.quadraticCurveTo(1680, 360, 2030, 520);
    c.stroke();

    c.restore();
  }

  private drawEntrance(): void {
    const c = this.ctx;
    c.save();
    const x = OBJECTS.entrance.x;
    const y = OBJECTS.entrance.y;

    c.fillStyle = '#6d5035';
    c.fillRect(x - 105, y - 54, 16, 150);
    c.fillRect(x + 90, y - 54, 16, 150);

    this.roundRect(x - 125, y - 112, 250, 86, 18);
    c.fillStyle = '#35583f';
    c.fill();
    c.strokeStyle = 'rgba(243,238,198,.42)';
    c.lineWidth = 3;
    c.stroke();

    c.fillStyle = '#f4f0d1';
    c.font = '900 28px sans-serif';
    c.textAlign = 'center';
    c.fillText('生命观察园', x, y - 75);
    c.font = '700 14px sans-serif';
    c.fillStyle = 'rgba(244,240,209,.78)';
    c.fillText('观察 · 比较 · 记录', x, y - 48);

    c.restore();
  }

  private drawRockGarden(state: AppState, now: number): void {
    const c = this.ctx;
    const t = state.clocks.lithops.t;
    const x = OBJECTS.lithops.x;
    const y = OBJECTS.lithops.y;

    c.save();
    c.fillStyle = 'rgba(94,77,52,.24)';
    c.beginPath();
    c.ellipse(820, 650, 310, 200, -0.08, 0, TAU);
    c.fill();

    for (const [sx, sy, rx, ry] of [
      [620, 585, 62, 40], [955, 575, 55, 34], [690, 730, 46, 30],
      [1020, 700, 67, 39], [875, 775, 50, 32]
    ] as number[][]) {
      c.fillStyle = '#7e795f';
      c.beginPath();
      c.ellipse(sx, sy, rx, ry, -0.18, 0, TAU);
      c.fill();
      c.strokeStyle = 'rgba(255,255,255,.10)';
      c.lineWidth = 3;
      c.stroke();
    }

    // Lithops stays stone-like at first, then visibly changes.
    const grow = smooth(0.40, 0.84, t);
    c.fillStyle = '#7d8d68';
    c.beginPath();
    c.ellipse(x - 24, y, 55 + grow * 8, 45 + grow * 5, -0.12, 0, TAU);
    c.fill();
    c.beginPath();
    c.ellipse(x + 32, y + 4, 54 + grow * 7, 44 + grow * 5, 0.12, 0, TAU);
    c.fill();

    c.strokeStyle = 'rgba(62,67,49,.52)';
    c.lineWidth = 3;
    c.beginPath();
    c.moveTo(x + 2, y - 34);
    c.lineTo(x + 3, y + 34);
    c.stroke();

    if (t > 0.66) {
      const bloom = smooth(0.66, 0.95, t);
      c.strokeStyle = '#56784d';
      c.lineWidth = 7;
      c.beginPath();
      c.moveTo(x + 4, y - 20);
      c.lineTo(x + 2, y - 20 - 54 * bloom);
      c.stroke();

      c.save();
      c.translate(x + 2, y - 76 * bloom);
      for (let i = 0; i < 9; i += 1) {
        c.rotate(TAU / 9);
        c.fillStyle = '#f5e68d';
        c.beginPath();
        c.ellipse(0, -18 * bloom, 7 * bloom, 20 * bloom, 0, 0, TAU);
        c.fill();
      }
      c.fillStyle = '#d9a73d';
      c.beginPath();
      c.arc(0, 0, 9 * bloom, 0, TAU);
      c.fill();
      c.restore();
    }

    c.restore();
  }

  private drawSeedlingPlot(state: AppState): void {
    const c = this.ctx;
    const t = state.clocks.seedlings.t;
    const x = OBJECTS.seedlings.x;
    const y = OBJECTS.seedlings.y;

    c.save();
    this.roundRect(x - 245, y - 150, 490, 280, 32);
    c.fillStyle = '#7a5639';
    c.fill();
    c.strokeStyle = 'rgba(241,218,169,.48)';
    c.lineWidth = 12;
    c.stroke();

    this.roundRect(x - 218, y - 125, 436, 230, 24);
    c.fillStyle = '#4f402d';
    c.fill();

    const g = smooth(0.1, 0.92, t);
    const leftX = x - 92;
    const rightX = x + 92;

    // Flat, ribbon-like leaves: a leek-like specimen.
    for (let i = -2; i <= 2; i += 1) {
      c.strokeStyle = i % 2 ? '#5ca861' : '#67b969';
      c.lineWidth = 10;
      c.lineCap = 'round';
      c.beginPath();
      c.moveTo(leftX + i * 14, y + 62);
      c.quadraticCurveTo(leftX + i * 19, y - 6 - g * 30, leftX + i * 9, y - 48 - g * 68);
      c.stroke();
    }

    // Narrower, more upright blades: a wheat-like specimen.
    for (let i = -3; i <= 3; i += 1) {
      c.strokeStyle = i % 2 ? '#76b760' : '#8bc76a';
      c.lineWidth = 6;
      c.lineCap = 'round';
      c.beginPath();
      c.moveTo(rightX + i * 12, y + 62);
      c.quadraticCurveTo(rightX + i * 8, y - 10 - g * 40, rightX + i * 4, y - 60 - g * 82);
      c.stroke();
    }

    if (t > 0.55) {
      c.fillStyle = 'rgba(244,240,209,.82)';
      c.font = '700 14px sans-serif';
      c.textAlign = 'center';
      c.fillText('持续观察后，细节越来越不同', x, y + 95);
    }

    c.restore();
  }

  private drawMimosa(state: AppState, now: number): void {
    const c = this.ctx;
    const x = OBJECTS.mimosa.x;
    const y = OBJECTS.mimosa.y;
    const folded = now - state.mimosaTouchedAt < 2600;
    const k = folded ? clamp((now - state.mimosaTouchedAt) / 300) : 0;

    c.save();
    c.strokeStyle = '#3e8655';
    c.lineWidth = 13;
    c.lineCap = 'round';
    c.beginPath();
    c.moveTo(x, y + 95);
    c.quadraticCurveTo(x - 16, y + 20, x + 4, y - 115);
    c.stroke();

    for (let row = 0; row < 5; row += 1) {
      const yy = y + 48 - row * 42;
      const side = row % 2 ? -1 : 1;
      const branchLen = folded ? 62 - k * 25 : 105;
      c.strokeStyle = '#438b55';
      c.lineWidth = 7;
      c.beginPath();
      c.moveTo(x, yy);
      c.lineTo(x + side * branchLen, yy - 8);
      c.stroke();

      for (let j = 0; j < 5; j += 1) {
        const bx = x + side * (20 + j * 18) * (folded ? 0.74 : 1);
        const by = yy - 7;
        c.save();
        c.translate(bx, by);
        c.rotate(side * (folded ? 0.72 : 0.18));
        c.fillStyle = '#77ba69';
        c.beginPath();
        c.ellipse(0, -7, folded ? 4 : 8, folded ? 16 : 23, 0, 0, TAU);
        c.fill();
        c.restore();
      }
    }

    c.fillStyle = '#d892c4';
    c.beginPath();
    c.arc(x + 5, y - 135, 24, 0, TAU);
    c.fill();
    c.fillStyle = '#eab9dd';
    for (let i = 0; i < 12; i += 1) {
      const a = i / 12 * TAU;
      c.beginPath();
      c.arc(x + 5 + Math.cos(a) * 22, y - 135 + Math.sin(a) * 22, 5, 0, TAU);
      c.fill();
    }
    c.restore();
  }

  private drawSeedBox(state: AppState): void {
    const c = this.ctx;
    const t = state.clocks.seed.t;
    const x = OBJECTS.seed.x;
    const y = OBJECTS.seed.y;

    c.save();
    this.roundRect(x - 210, y - 135, 420, 250, 28);
    c.fillStyle = 'rgba(225,239,220,.18)';
    c.fill();
    c.strokeStyle = 'rgba(236,246,226,.55)';
    c.lineWidth = 9;
    c.stroke();

    c.fillStyle = '#5f462f';
    c.fillRect(x - 180, y + 40, 360, 58);

    const germ = smooth(0.18, 0.72, t);
    c.save();
    c.translate(x, y + 42);
    c.fillStyle = '#a77b4c';
    c.beginPath();
    c.ellipse(0, 0, 34, 22, -0.2, 0, TAU);
    c.fill();

    c.strokeStyle = '#f2e6c1';
    c.lineWidth = 8;
    c.lineCap = 'round';
    c.beginPath();
    c.moveTo(4, 10);
    c.quadraticCurveTo(-10, 28 + 42 * germ, -28, 44 + 72 * germ);
    c.stroke();

    c.strokeStyle = '#67a85b';
    c.lineWidth = 10;
    c.beginPath();
    c.moveTo(0, -5);
    c.quadraticCurveTo(0, -36 * germ, 2, -85 * germ);
    c.stroke();

    if (germ > 0.22) {
      c.fillStyle = '#79bd68';
      c.save();
      c.translate(1, -78 * germ);
      c.rotate(-0.5);
      c.beginPath();
      c.ellipse(-16, 0, 23 * germ, 11 * germ, 0, 0, TAU);
      c.fill();
      c.restore();
      c.save();
      c.translate(2, -78 * germ);
      c.rotate(0.5);
      c.beginPath();
      c.ellipse(16, 0, 23 * germ, 11 * germ, 0, 0, TAU);
      c.fill();
      c.restore();
    }
    c.restore();

    c.restore();
  }

  private drawFlowerPlot(state: AppState, now: number): void {
    const c = this.ctx;
    const t = state.clocks.flower.t;
    const x = OBJECTS.flower.x;
    const y = OBJECTS.flower.y;

    c.save();
    c.fillStyle = 'rgba(65,95,47,.38)';
    c.beginPath();
    c.ellipse(x, y + 24, 250, 205, 0, 0, TAU);
    c.fill();

    const stemH = 130 + smooth(0.1, 0.45, t) * 70;
    c.strokeStyle = '#44834a';
    c.lineWidth = 13;
    c.lineCap = 'round';
    c.beginPath();
    c.moveTo(x, y + 125);
    c.lineTo(x, y + 125 - stemH);
    c.stroke();

    c.fillStyle = '#6aa65d';
    for (const s of [-1, 1]) {
      c.save();
      c.translate(x, y + 52);
      c.rotate(s * 0.7);
      c.beginPath();
      c.ellipse(s * 42, 0, 50, 20, 0, 0, TAU);
      c.fill();
      c.restore();
    }

    const topY = y + 125 - stemH;
    const bloom = smooth(0.30, 0.62, t);
    const fruit = smooth(0.68, 0.92, t);

    if (fruit < 0.68) {
      c.save();
      c.translate(x, topY);
      for (let i = 0; i < 7; i += 1) {
        c.rotate(TAU / 7);
        c.fillStyle = '#f3bfd0';
        c.beginPath();
        c.ellipse(0, -30 * bloom, 15 * bloom, 34 * bloom, 0, 0, TAU);
        c.fill();
      }
      c.fillStyle = '#d6a444';
      c.beginPath();
      c.arc(0, 0, 13 * bloom, 0, TAU);
      c.fill();
      c.restore();
    }

    if (t > 0.68) {
      c.fillStyle = '#9b5237';
      c.beginPath();
      c.ellipse(x, topY + 10, 34 * fruit, 42 * fruit, 0, 0, TAU);
      c.fill();
      c.fillStyle = '#f1d7a8';
      for (let i = 0; i < 5; i += 1) {
        const a = i / 5 * TAU;
        c.beginPath();
        c.ellipse(x + Math.cos(a) * 18 * fruit, topY + 10 + Math.sin(a) * 24 * fruit, 5, 9, a, 0, TAU);
        c.fill();
      }
    }

    // Nearby flowers make the plot read as a living patch, not one diagram.
    for (let i = 0; i < 6; i += 1) {
      const a = i / 6 * TAU + 0.4;
      const px = x + Math.cos(a) * 125;
      const py = y + Math.sin(a) * 86 + 40;
      c.strokeStyle = '#4f8d4e';
      c.lineWidth = 7;
      c.beginPath();
      c.moveTo(px, py + 30);
      c.lineTo(px, py - 24);
      c.stroke();
      c.fillStyle = i % 2 ? '#e9b6cb' : '#f1d88a';
      c.beginPath();
      c.arc(px, py - 33, 14, 0, TAU);
      c.fill();
    }

    c.restore();
  }

  private drawPond(state: AppState, now: number): void {
    const c = this.ctx;
    const x = OBJECTS.pond.x;
    const y = OBJECTS.pond.y;

    c.save();
    c.fillStyle = '#435f3b';
    c.beginPath();
    c.ellipse(x, y, 580, 405, -0.08, 0, TAU);
    c.fill();

    const water = c.createRadialGradient(x - 120, y - 130, 60, x, y, 530);
    water.addColorStop(0, '#70b3a1');
    water.addColorStop(1, '#3e7d72');
    c.fillStyle = water;
    c.beginPath();
    c.ellipse(x, y, 515, 350, -0.08, 0, TAU);
    c.fill();

    c.strokeStyle = 'rgba(218,245,225,.18)';
    c.lineWidth = 4;
    for (let i = 0; i < 8; i += 1) {
      const yy = y - 210 + i * 58;
      const phase = Math.sin(now * 0.001 + i) * 16;
      c.beginPath();
      c.moveTo(x - 270 + phase, yy);
      c.quadraticCurveTo(x, yy + 12, x + 250 + phase, yy - 5);
      c.stroke();
    }

    for (let i = 0; i < 9; i += 1) {
      const a = (i / 9) * TAU;
      const px = x + Math.cos(a) * 435;
      const py = y + Math.sin(a) * 292;
      c.strokeStyle = '#567e45';
      c.lineWidth = 8;
      c.beginPath();
      c.moveTo(px, py + 20);
      c.lineTo(px + Math.cos(a) * 10, py - 48);
      c.stroke();
      c.fillStyle = '#75955d';
      c.beginPath();
      c.ellipse(px + Math.cos(a) * 10, py - 55, 9, 34, a, 0, TAU);
      c.fill();
    }

    this.drawDuck(state, now);
    this.drawDuckFamily(state);
    this.drawWaste(state);
    c.restore();
  }

  private drawDuck(state: AppState, now: number): void {
    const c = this.ctx;
    if (state.clocks.duck.t < 0.28) return;

    const d = state.duck;
    const breathing = !d.swimmingAway && state.clocks.duck.t > 0.58;
    const breath = breathing ? 1 + Math.sin(now * 0.006) * 0.035 : 1;
    const feedingCycle = (now % 12000) / 12000;
    const feeding = feedingCycle > 0.16 && feedingCycle < 0.42 && !d.swimmingAway;

    c.save();
    c.translate(d.x, d.y);
    if (d.swimmingAway) c.rotate(Math.atan2(d.vy, d.vx));

    c.fillStyle = 'rgba(20,63,55,.28)';
    c.beginPath();
    c.ellipse(0, 58, 88, 24, 0, 0, TAU);
    c.fill();

    c.save();
    c.scale(breath, 1 / breath);
    c.fillStyle = '#f6e9c8';
    c.beginPath();
    c.ellipse(0, 0, 76, 54, -0.08, 0, TAU);
    c.fill();
    c.restore();

    c.fillStyle = '#8b603f';
    c.beginPath();
    c.arc(56, -34, 35, 0, TAU);
    c.fill();

    c.fillStyle = '#f1a94d';
    c.beginPath();
    c.moveTo(84, -30);
    c.lineTo(126, -20);
    c.lineTo(86, -8);
    c.closePath();
    c.fill();

    c.fillStyle = '#171917';
    c.beginPath();
    c.arc(67, -43, 4, 0, TAU);
    c.fill();

    c.fillStyle = '#d5c7a5';
    c.beginPath();
    c.ellipse(-22, 0, 40, 25, 0.2, 0, TAU);
    c.fill();

    if (feeding) {
      c.fillStyle = '#6aa658';
      for (let i = 0; i < 3; i += 1) {
        c.beginPath();
        c.ellipse(115 + i * 15, -10 + i * 4, 11, 6, 0.5, 0, TAU);
        c.fill();
      }
    }
    c.restore();
  }

  private drawDuckFamily(state: AppState): void {
    if (state.clocks.duck.t < 0.86) return;
    const c = this.ctx;
    const baseX = OBJECTS.duckNest.x;
    const baseY = OBJECTS.duckNest.y;
    c.save();
    c.fillStyle = '#826a3e';
    c.beginPath();
    c.ellipse(baseX, baseY, 124, 48, -0.08, 0, TAU);
    c.fill();
    for (let i = 0; i < 3; i += 1) {
      const x = baseX - 55 + i * 56;
      const y = baseY - 36 - (i % 2) * 12;
      c.fillStyle = '#d8ba68';
      c.beginPath();
      c.ellipse(x, y, 28, 22, 0, 0, TAU);
      c.fill();
      c.beginPath();
      c.arc(x + 18, y - 15, 13, 0, TAU);
      c.fill();
      c.fillStyle = '#7b5b43';
      c.beginPath();
      c.arc(x - 8 + i * 4, y - 3, 5 + i, 0, TAU);
      c.fill();
    }
    c.restore();
  }

  private drawWaste(state: AppState): void {
    if (!state.duckWasteSeen) return;
    const c = this.ctx;
    c.save();
    c.fillStyle = '#5f5638';
    c.beginPath();
    c.ellipse(2280, 920, 20, 11, 0, 0, TAU);
    c.fill();
    c.fillStyle = 'rgba(247,245,227,.78)';
    c.font = '700 12px sans-serif';
    c.textAlign = 'center';
    c.fillText('观察到新的痕迹', 2280, 950);
    c.restore();
  }

  private drawMicroscopeStation(state: AppState, now: number): void {
    const c = this.ctx;
    const x = OBJECTS.microscope.x;
    const y = OBJECTS.microscope.y;
    const unlocked = this.coreCount(state) >= 5;

    c.save();
    this.roundRect(x - 220, y - 135, 440, 250, 34);
    c.fillStyle = '#d8d0ad';
    c.fill();
    c.strokeStyle = 'rgba(89,73,46,.30)';
    c.lineWidth = 8;
    c.stroke();

    c.fillStyle = '#75583c';
    c.fillRect(x - 180, y + 65, 360, 30);

    c.strokeStyle = unlocked ? '#345e54' : '#6e6b5a';
    c.lineWidth = 24;
    c.lineCap = 'round';
    c.beginPath();
    c.moveTo(x - 36, y + 36);
    c.quadraticCurveTo(x + 28, y - 38, x + 3, y - 84);
    c.stroke();
    c.fillStyle = unlocked ? '#477a6d' : '#7d7c6b';
    c.beginPath();
    c.ellipse(x + 7, y - 91, 36, 21, -0.9, 0, TAU);
    c.fill();
    c.fillStyle = '#222b28';
    c.beginPath();
    c.arc(x - 39, y + 44, 28, 0, TAU);
    c.fill();

    if (unlocked) {
      const pulse = 0.72 + Math.sin(now * 0.004) * 0.12;
      c.strokeStyle = `rgba(244,242,202,${pulse})`;
      c.lineWidth = 5;
      c.beginPath();
      c.arc(x, y, 150, 0, TAU);
      c.stroke();
    }

    c.restore();
  }

  private drawPlayer(state: AppState, now: number): void {
    const c = this.ctx;
    const p = state.player;
    const walking = Math.hypot(p.vx, p.vy) > 18;
    const step = walking ? Math.sin(now * 0.012) : 0;

    c.save();
    c.translate(p.x, p.y);

    c.fillStyle = 'rgba(32,60,37,.28)';
    c.beginPath();
    c.ellipse(0, 54, 58, 18, 0, 0, TAU);
    c.fill();

    // Backpack.
    c.fillStyle = '#d89955';
    this.roundRect(-42, -2, 34, 57, 14);
    c.fill();

    // Body.
    c.fillStyle = '#4d9b7f';
    this.roundRect(-28, -18, 58, 77, 22);
    c.fill();

    // Legs.
    c.strokeStyle = '#f2d7b5';
    c.lineWidth = 13;
    c.lineCap = 'round';
    c.beginPath();
    c.moveTo(-12, 52);
    c.lineTo(-17 + step * 7, 83);
    c.moveTo(13, 52);
    c.lineTo(17 - step * 7, 83);
    c.stroke();

    // Face.
    c.fillStyle = '#f1d6b5';
    c.beginPath();
    c.arc(0, -48, 31, 0, TAU);
    c.fill();

    // Hair + small side ponytail, giving 豆芽 a recognizable silhouette.
    c.fillStyle = '#4a3029';
    c.beginPath();
    c.arc(0, -57, 33, Math.PI, TAU);
    c.lineTo(32, -47);
    c.lineTo(-32, -47);
    c.closePath();
    c.fill();
    c.beginPath();
    c.arc(28, -52, 11, 0, TAU);
    c.fill();

    // Scarf.
    c.strokeStyle = '#e8b65d';
    c.lineWidth = 8;
    c.beginPath();
    c.moveTo(-20, -20);
    c.lineTo(15, -14);
    c.stroke();

    c.fillStyle = 'rgba(29,58,39,.78)';
    this.roundRect(-35, -111, 70, 29, 14);
    c.fill();
    c.fillStyle = '#f4f7df';
    c.font = '900 17px sans-serif';
    c.textAlign = 'center';
    c.fillText('豆芽', 0, -91);
    c.restore();
  }

  private drawMicroscope(state: AppState, now: number): void {
    const c = this.ctx;
    const w = state.w;
    const h = state.h;
    const age = clamp((now - state.microscopeOpenedAt) / 1200);

    const bg = c.createRadialGradient(w * 0.5, h * 0.46, 30, w * 0.5, h * 0.46, Math.max(w, h) * 0.62);
    bg.addColorStop(0, '#f3edc8');
    bg.addColorStop(0.62, '#bdcf9a');
    bg.addColorStop(1, '#355d48');
    c.fillStyle = bg;
    c.fillRect(0, 0, w, h);

    c.save();
    c.beginPath();
    c.arc(w / 2, h * 0.47, Math.min(w, h) * 0.42, 0, TAU);
    c.clip();

    const size = lerp(110, 72, smooth(0, 1, age));
    for (let row = -1; row < Math.ceil(h / size) + 1; row += 1) {
      for (let col = -1; col < Math.ceil(w / size) + 1; col += 1) {
        const x = col * size + (row % 2) * size * 0.16 + (now * 0.004 % size);
        const y = row * size;
        const jitter = Math.sin(col * 1.7 + row * 2.1) * 6;
        c.fillStyle = (row + col) % 2 ? 'rgba(167,194,118,.44)' : 'rgba(196,212,140,.42)';
        c.strokeStyle = 'rgba(69,100,61,.72)';
        c.lineWidth = 3;
        c.beginPath();
        c.moveTo(x + 6, y + 10);
        c.lineTo(x + size - 7 + jitter, y + 5);
        c.lineTo(x + size - 5, y + size - 11);
        c.lineTo(x + 9 - jitter, y + size - 5);
        c.closePath();
        c.fill();
        c.stroke();

        c.fillStyle = 'rgba(66,116,58,.62)';
        c.beginPath();
        c.arc(x + size * 0.62, y + size * 0.48, 7, 0, TAU);
        c.fill();
      }
    }
    c.restore();

    c.strokeStyle = 'rgba(250,246,220,.94)';
    c.lineWidth = Math.max(8, Math.min(w, h) * 0.026);
    c.beginPath();
    c.arc(w / 2, h * 0.47, Math.min(w, h) * 0.42, 0, TAU);
    c.stroke();
    c.strokeStyle = 'rgba(18,52,39,.58)';
    c.lineWidth = 3;
    c.stroke();

    c.fillStyle = 'rgba(6,27,20,.78)';
    this.roundRect(w * 0.5 - Math.min(310, w * 0.39), h * 0.79, Math.min(620, w * 0.78), 84, 18);
    c.fill();
    c.fillStyle = '#f5f4df';
    c.font = `900 ${Math.max(17, Math.min(27, w * 0.05))}px sans-serif`;
    c.textAlign = 'center';
    c.fillText('叶片组织由许多细胞组成', w / 2, h * 0.79 + 36);
    c.font = `700 ${Math.max(11, Math.min(15, w * 0.031))}px sans-serif`;
    c.fillStyle = 'rgba(245,244,223,.72)';
    c.fillText('显微示意 · 颜色和比例用于帮助观察', w / 2, h * 0.79 + 62);

    this.drawVignette(w, h);
  }

  private drawWorldLabel(x: number, y: number, text: string): void {
    const c = this.ctx;
    c.save();
    c.font = '800 15px sans-serif';
    const padX = 15;
    const width = c.measureText(text).width + padX * 2;
    this.roundRect(x - width / 2, y - 24, width, 38, 16);
    c.fillStyle = 'rgba(22,53,37,.68)';
    c.fill();
    c.fillStyle = 'rgba(248,249,225,.88)';
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    c.fillText(text, x, y - 5);
    c.restore();
  }

  private drawVignette(w: number, h: number): void {
    const c = this.ctx;
    const g = c.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.24, w / 2, h / 2, Math.max(w, h) * 0.72);
    g.addColorStop(0, 'rgba(0,0,0,0)');
    g.addColorStop(1, 'rgba(3,20,13,.22)');
    c.fillStyle = g;
    c.fillRect(0, 0, w, h);
  }

  private coreCount(state: AppState): number {
    const keys = ['nutrition', 'respiration', 'stimulus', 'growth', 'reproduction', 'heredityVariation', 'waste'] as const;
    return keys.reduce((n, key) => n + (state.evidence[key] ? 1 : 0), 0);
  }

  private roundRect(x: number, y: number, w: number, h: number, r: number): void {
    const c = this.ctx;
    const rr = Math.min(r, w / 2, h / 2);
    c.beginPath();
    c.moveTo(x + rr, y);
    c.arcTo(x + w, y, x + w, y + h, rr);
    c.arcTo(x + w, y + h, x, y + h, rr);
    c.arcTo(x, y + h, x, y, rr);
    c.arcTo(x, y, x + w, y, rr);
    c.closePath();
  }
}